import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, Clock, Edit2, Check, X,
  AlertTriangle, CheckCircle, Mail, Building, MapPin
} from 'lucide-react';
import { getContact, updatePipeline, updateNotes, setFollowUp } from '../services/api';
import { formatDate, formatDuration, timeAgo, callTypeBadge, urgencyBadge, pipelineLabel, pipelineBadge } from '../utils';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PIPELINE_STAGES = ['new_lead', 'contacted', 'proposal_sent', 'won', 'lost'];

// Editable field with AI verification badge
function EditableField({ label, value, field, contactId, verified, onUpdate, icon: Icon, type = 'text', placeholder }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(verified);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.patch(`${API_BASE}/contacts/${contactId}/verify`, {
        field,
        value: inputVal
      });
      if (res.data.success) {
        onUpdate(field, inputVal);
        setIsVerified(true);
        setEditing(false);
      } else {
        setError(res.data.errors?.[0] || 'Validation failed');
      }
    } catch (e) {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const needsVerification = !isVerified && value;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 10,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        {Icon && <Icon size={10} />}
        {label}
        {needsVerification && (
          <span style={{
            background: 'rgba(245,158,11,0.15)',
            color: 'var(--orange)',
            fontSize: 9,
            padding: '1px 6px',
            borderRadius: 99,
            fontWeight: 600,
          }}>
            AI extracted — verify
          </span>
        )}
        {isVerified && value && (
          <span style={{
            background: 'rgba(16,185,129,0.15)',
            color: 'var(--green)',
            fontSize: 9,
            padding: '1px 6px',
            borderRadius: 99,
            fontWeight: 600,
          }}>
            ✓ Verified
          </span>
        )}
      </div>

      {editing ? (
        <div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              type={type}
              placeholder={placeholder || `Enter ${label.toLowerCase()}`}
              style={{
                flex: 1,
                background: 'var(--bg-primary)',
                border: `1px solid ${error ? 'var(--red)' : 'var(--blue)'}`,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                padding: '6px 10px',
                fontSize: 13,
              }}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              autoFocus
            />
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '6px 10px' }}
            >
              <Check size={13} />
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => { setEditing(false); setError(''); setInputVal(value || ''); }}
              style={{ padding: '6px 10px' }}
            >
              <X size={13} />
            </button>
          </div>
          {error && (
            <p style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>{error}</p>
          )}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            padding: '6px 8px',
            borderRadius: 'var(--radius-sm)',
            border: needsVerification
              ? '1px solid rgba(245,158,11,0.3)'
              : '1px solid transparent',
            background: needsVerification
              ? 'rgba(245,158,11,0.05)'
              : 'transparent',
            transition: 'all 0.15s',
            minWidth: 0,
          }}
          onClick={() => setEditing(true)}
          title="Click to edit"
        >
          <span style={{
            fontSize: 14,
            color: value ? 'var(--text-primary)' : 'var(--text-muted)',
            flex: 1,
            minWidth: 0,
            overflowWrap: 'break-word',
          }}>
            {value || `Add ${label.toLowerCase()}...`}
          </span>
          <Edit2 size={12} color="var(--text-muted)" style={{ opacity: 0, transition: 'opacity 0.15s' }}
            className="edit-icon" />
        </div>
      )}
    </div>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifiedFields, setVerifiedFields] = useState({});
  const [calling, setCalling] = useState(false);
  const [callMessage, setCallMessage] = useState('');
  useEffect(() => {
    getContact(id).then(res => {
      setData(res.data);
      setNotes(res.data.contact?.notes || '');
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleFieldUpdate = (field, value) => {
    setData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
    setVerifiedFields(prev => ({ ...prev, [field]: true }));
  };

  const handleStageChange = async (stage) => {
    try {
      await updatePipeline(id, stage);
      setData(prev => ({
        ...prev,
        contact: { ...prev.contact, pipeline_stage: stage }
      }));
    } catch (e) { console.error(e); }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await updateNotes(id, notes);
      setData(prev => ({
        ...prev,
        contact: { ...prev.contact, notes }
      }));
      setEditingNotes(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleCallBack = async () => {
    setCalling(true);
    setCallMessage('');
    try {
      const token = localStorage.getItem('cia_token');
      const res = await axios.post(
        `${API_BASE}/calls/call-contact/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setCallMessage(res.data.message || 'Calling your phone now — answer it to connect.');
      } else {
        setCallMessage(res.data.error || 'Could not start call.');
      }
    } catch (e) {
      setCallMessage(e.response?.data?.error || 'Could not start call.');
    } finally {
      setCalling(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!data?.contact) return (
    <div style={{ padding: 28 }}>
      <button className="btn btn-ghost" onClick={() => navigate('/contacts')}>
        <ArrowLeft size={14} /> Back
      </button>
      <div className="empty-state"><p>Contact not found</p></div>
    </div>
  );

  const { contact, calls = [] } = data;

  return (
    <div className="page-wrap" style={{ maxWidth: 900 }}>
      <button className="btn btn-ghost" onClick={() => navigate('/contacts')} style={{ marginBottom: 20 }}>
        <ArrowLeft size={14} /> Back to Contacts
      </button>

      <div className="detail-grid">

        {/* Left column */}
        <div>

          {/* Contact Header */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{
                width: 52, height: 52,
                background: 'var(--blue)',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, fontWeight: 700, color: 'white',
                flexShrink: 0,
              }}>
                {(contact.name || contact.phone || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ fontSize: 20, fontWeight: 700, overflowWrap: 'break-word' }}>{contact.name || 'Unknown Caller'}</h1>
                {contact.company && <p style={{ color: 'var(--text-secondary)', fontSize: 14, overflowWrap: 'break-word' }}>{contact.company}</p>}
                <p style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--mono)', overflowWrap: 'break-word' }}>{contact.phone}</p>
              </div>
              <button
                onClick={handleCallBack}
                disabled={calling || !contact.phone}
                className="btn btn-primary"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 18px', fontSize: 14, fontWeight: 600,
                  opacity: calling ? 0.7 : 1,
                  cursor: calling || !contact.phone ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                }}
              >
                <Phone size={15} />
                {calling ? 'Calling...' : 'Call'}
              </button>
            </div>

            {callMessage && (
              <div style={{
                marginBottom: 16,
                padding: '10px 14px',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(26,86,219,0.08)',
                border: '1px solid rgba(26,86,219,0.3)',
                fontSize: 13,
                color: 'var(--text-secondary)',
              }}>
                📱 {callMessage}
              </div>
            )}

            {/* Editable fields */}
            <EditableField
              label="Full Name"
              value={contact.name}
              field="name"
              contactId={id}
              verified={verifiedFields.name}
              onUpdate={handleFieldUpdate}
              icon={Phone}
              placeholder="Enter full name"
            />
            <EditableField
              label="Company"
              value={contact.company}
              field="company"
              contactId={id}
              verified={verifiedFields.company}
              onUpdate={handleFieldUpdate}
              icon={Building}
              placeholder="Enter company name"
            />
            <EditableField
              label="Email Address"
              value={contact.email}
              field="email"
              contactId={id}
              verified={verifiedFields.email}
              onUpdate={handleFieldUpdate}
              icon={Mail}
              type="email"
              placeholder="Enter email address"
            />

            {/* Stats */}
            <div style={{
              display: 'flex', gap: 24, marginTop: 16,
              paddingTop: 16, borderTop: '1px solid var(--border)', flexWrap: 'wrap',
            }}>
              {[
                { label: 'Total Calls', value: contact.total_calls || 0 },
                { label: 'Last Called', value: timeAgo(contact.last_called_at) },
                { label: 'Contact Since', value: timeAgo(contact.created_at) },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 14, fontWeight: 600 }}>Notes</h2>
              {!editingNotes ? (
                <button className="btn btn-ghost" onClick={() => setEditingNotes(true)} style={{ fontSize: 12 }}>
                  <Edit2 size={12} /> Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary" onClick={handleSaveNotes} disabled={saving} style={{ fontSize: 12 }}>
                    <Check size={12} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setEditingNotes(false)} style={{ fontSize: 12 }}>
                    <X size={12} /> Cancel
                  </button>
                </div>
              )}
            </div>
            {editingNotes ? (
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add notes about this contact..."
                rows={4}
                style={{
                  width: '100%',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  padding: 12, fontSize: 13, resize: 'vertical', lineHeight: 1.6,
                }}
              />
            ) : (
              <p style={{ fontSize: 13, color: contact.notes ? 'var(--text-secondary)' : 'var(--text-muted)', lineHeight: 1.7, overflowWrap: 'break-word' }}>
                {contact.notes || 'No notes yet. Click Edit to add notes.'}
              </p>
            )}
          </div>

          {/* Call History */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <h2 style={{ fontSize: 14, fontWeight: 600 }}>
                Call History <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({calls.length})</span>
              </h2>
            </div>
            {calls.length === 0 ? (
              <div className="empty-state">
                <Phone size={28} />
                <p>No calls yet</p>
              </div>
            ) : calls.map((call, idx) => {
              const typeBadge = callTypeBadge(call.call_type, call);
              const urgBadge = urgencyBadge(call.urgency);
              return (
                <div
                  key={call.id}
                  onClick={() => navigate(`/calls/${call.id}`)}
                  style={{
                    padding: '16px 20px',
                    borderBottom: idx < calls.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className={`badge ${typeBadge.class}`}>{typeBadge.label}</span>
                      <span className={`badge ${urgBadge.class}`}>{urgBadge.label}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>
                        {formatDuration(call.duration)}
                      </span>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(call.created_at)}</span>
                  </div>
                  {call.summary && (
                    <p style={{
                      fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.6,
                      overflow: 'hidden', display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {call.summary.split('\n')[0]}
                    </p>
                  )}
                  {call.suggested_action && (
                    <p style={{ fontSize: 12, color: 'var(--green)', marginTop: 6 }}>
                      ✅ {call.suggested_action}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Pipeline Stage */}
          <div className="card">
            <h2 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pipeline Stage
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {PIPELINE_STAGES.map(stage => (
                <button
                  key={stage}
                  onClick={() => handleStageChange(stage)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-sm)',
                    border: contact.pipeline_stage === stage
                      ? '1px solid var(--blue)'
                      : '1px solid var(--border)',
                    background: contact.pipeline_stage === stage
                      ? 'rgba(26,86,219,0.15)'
                      : 'transparent',
                    color: contact.pipeline_stage === stage
                      ? 'var(--blue-light)'
                      : 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: contact.pipeline_stage === stage ? 600 : 400,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: contact.pipeline_stage === stage ? 'var(--blue)' : 'var(--border)',
                  }} />
                  {pipelineLabel(stage)}
                </button>
              ))}
            </div>
          </div>

          {/* Follow-up */}
          <div className="card">
            <h2 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Follow-up
            </h2>
            {contact.follow_up_date ? (
              <div>
                <div style={{
                  fontSize: 13,
                  color: new Date(contact.follow_up_date) < new Date() ? 'var(--red)' : 'var(--orange)',
                  fontWeight: 600,
                  marginBottom: 4,
                }}>
                  {new Date(contact.follow_up_date) < new Date() ? '⏰ Overdue' : '🔔 Scheduled'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {formatDate(contact.follow_up_date)}
                </div>
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No follow-up scheduled</p>
            )}
          </div>

          {/* Data Quality */}
          <div className="card" style={{ border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.05)' }}>
            <h2 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--orange)' }}>
              ⚡ Data Quality
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Name', value: contact.name, key: 'name' },
                { label: 'Company', value: contact.company, key: 'company' },
                { label: 'Email', value: contact.email, key: 'email' },
                { label: 'Phone', value: contact.phone, key: 'phone' },
              ].map(field => (
                <div key={field.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{field.label}</span>
                  {field.value ? (
                    verifiedFields[field.key] || field.key === 'phone' ? (
                      <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ OK</span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--orange)' }}>⚠ Verify</span>
                    )
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Missing</span>
                  )}
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
              Click any field above to verify or correct AI-extracted data.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
